import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { RAG, canEdit, canManage } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Trash2, Filter, BellRing, Sparkles, CheckSquare, Square, FolderOpen, Upload } from "lucide-react";
import { useSearchParams } from "react-router-dom";

const emptyAction = { theme_key: "", title: "", description: "", owner: "", owner_id: null, due_date: "", rag_status: "red", progress: 0, priority: "medium" };

export default function Actions() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [themes, setThemes] = useState([]);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [allEvidence, setAllEvidence] = useState([]);
  const [filter, setFilter] = useState({ theme: searchParams.get("theme") || "all", rag: "all" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyAction, theme_key: searchParams.get("theme") || "" });
  const [busy, setBusy] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestTheme, setSuggestTheme] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [suggestOwner, setSuggestOwner] = useState("");
  const [suggestBusy, setSuggestBusy] = useState(false);

  const load = async () => {
    const [t, a, u, e] = await Promise.all([api.get("/themes"), api.get("/actions"), api.get("/users"), api.get("/evidence")]);
    setThemes(t.data); setActions(a.data); setUsers(u.data); setAllEvidence(e.data);
    if (!form.theme_key && t.data.length) setForm((f) => ({ ...f, theme_key: t.data[0].key }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Auto-open suggestions if ?suggest=1
  useEffect(() => {
    if (searchParams.get("suggest") === "1" && themes.length && !suggestOpen) {
      const key = searchParams.get("theme") || themes[0].key;
      openSuggestions(key);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [themes]);

  const filtered = actions.filter((a) => (filter.theme === "all" || a.theme_key === filter.theme) && (filter.rag === "all" || a.rag_status === filter.rag));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/actions", form);
      toast.success("Action created");
      setOpen(false); setForm({ ...emptyAction, theme_key: form.theme_key });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const updateAction = async (id, patch) => {
    try { await api.patch(`/actions/${id}`, patch); load(); toast.success("Updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete action?")) return;
    await api.delete(`/actions/${id}`); load(); toast.success("Deleted");
  };

  const sendReminders = async () => {
    try {
      const { data } = await api.post("/reminders/send?days=7");
      toast.success(`Reminders queued for ${data.sent.length} recipient(s) · ${data.total_actions} actions`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openSuggestions = async (themeKey) => {
    setSuggestTheme(themeKey); setPicked(new Set()); setSuggestOwner("");
    try {
      const { data } = await api.get(`/themes/${themeKey}/suggested-actions`);
      setSuggestions(data); setSuggestOpen(true);
    } catch (e) { toast.error("Failed to load suggestions"); }
  };

  const togglePick = (i) => { const s = new Set(picked); s.has(i) ? s.delete(i) : s.add(i); setPicked(s); };
  const pickAll = () => setPicked(new Set(suggestions.map((_, i) => i)));

  const createFromSuggestions = async () => {
    if (picked.size === 0) { toast.error("Pick at least one action"); return; }
    setSuggestBusy(true);
    try {
      const { data } = await api.post("/actions/from-suggestions", { theme_key: suggestTheme, indices: Array.from(picked), default_owner: suggestOwner });
      toast.success(`${data.count} suggested action(s) added to your QIP`);
      setSuggestOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSuggestBusy(false); }
  };

  const importCsv = async (file) => {
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/actions/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} action(s). ${data.skipped.length} skipped.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
  };

  const themeTitle = (k) => themes.find((t) => t.key === k)?.title || k;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-indigo-800">QIP Actions</div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>Improvement tracker</h1>
          <p className="text-slate-600 mt-1">Plan, own and evidence every improvement action across the seven Ofsted areas.</p>
        </div>
        {canEdit(user?.role) && (
          <div className="flex gap-2 flex-wrap">
            {canManage(user?.role) && <Button data-testid="send-reminders-btn" onClick={sendReminders} variant="outline"><BellRing className="h-4 w-4 mr-1.5" />Email reminders (7 days)</Button>}
            {canManage(user?.role) && (
              <>
                <input id="csv-input" type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files?.[0] && importCsv(e.target.files[0])} data-testid="csv-input" />
                <Button data-testid="csv-import-btn" onClick={() => document.getElementById("csv-input").click()} variant="outline"><Upload className="h-4 w-4 mr-1.5" />CSV import</Button>
              </>
            )}
            <Button data-testid="suggest-actions-btn" onClick={() => openSuggestions(filter.theme !== "all" ? filter.theme : (themes[0]?.key || "safeguarding"))} variant="outline" className="border-indigo-300 text-indigo-800 hover:bg-indigo-50"><Sparkles className="h-4 w-4 mr-1.5" />Ofsted-aligned suggestions</Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-action-btn" className="bg-indigo-900 hover:bg-indigo-950 text-white"><Plus className="h-4 w-4 mr-1.5" />New action</Button></DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle>New QIP action</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Theme</Label>
                  <Select value={form.theme_key} onValueChange={(v) => setForm({ ...form, theme_key: v })}>
                    <SelectTrigger data-testid="new-action-theme"><SelectValue /></SelectTrigger>
                    <SelectContent>{themes.map((t) => <SelectItem key={t.key} value={t.key}>{t.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Title</Label><Input data-testid="new-action-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>Description</Label><Textarea data-testid="new-action-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Owner</Label>
                    <Select value={form.owner_id || "_none"} onValueChange={(v) => {
                      if (v === "_none") { setForm({ ...form, owner_id: null, owner: "" }); return; }
                      const u = users.find((x) => x.id === v);
                      setForm({ ...form, owner_id: v, owner: u?.name || "" });
                    }}>
                      <SelectTrigger data-testid="new-action-owner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Unassigned</SelectItem>
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Due date</Label><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>RAG</Label>
                    <Select value={form.rag_status} onValueChange={(v) => setForm({ ...form, rag_status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{RAG.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter><Button data-testid="save-action-btn" onClick={submit} disabled={busy || !form.title || !form.theme_key} className="bg-indigo-900 hover:bg-indigo-950 text-white">{busy ? "Saving…" : "Create"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <Card className="p-4 flex items-center gap-3 flex-wrap border-slate-200">
        <Filter className="h-4 w-4 text-slate-500" />
        <Select value={filter.theme} onValueChange={(v) => setFilter({ ...filter, theme: v })}>
          <SelectTrigger data-testid="filter-theme" className="w-56"><SelectValue placeholder="All themes" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All themes</SelectItem>{themes.map((t) => <SelectItem key={t.key} value={t.key}>{t.title}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filter.rag} onValueChange={(v) => setFilter({ ...filter, rag: v })}>
          <SelectTrigger data-testid="filter-rag" className="w-40"><SelectValue placeholder="All RAG" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All RAG</SelectItem>{RAG.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="text-xs text-slate-500 ml-auto font-mono">{filtered.length} actions</div>
      </Card>

      <div className="space-y-3">
        {filtered.length === 0 && <Card className="p-8 text-center text-slate-500">No actions yet. Add one to get started.</Card>}
        {filtered.map((a) => {
          const linkedEv = allEvidence.filter((e) => (e.action_ids || []).includes(a.id));
          return (
          <Card key={a.id} data-testid={`action-row-${a.id}`} className="p-4 border-slate-200">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full rag-dot-${a.rag_status}`} />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{themeTitle(a.theme_key)}</span>
                  {a.priority === "high" && <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px]">HIGH</Badge>}
                  {linkedEv.length > 0 && <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-800"><FolderOpen className="h-3 w-3 mr-1" />{linkedEv.length} evidence</Badge>}
                </div>
                <div className="font-semibold text-slate-900 mt-1">{a.title}</div>
                {a.description && <div className="text-sm text-slate-600 mt-1">{a.description}</div>}
                <div className="mt-2 text-xs text-slate-500 flex gap-3 flex-wrap">
                  <span>Owner: <b className="text-slate-800">{a.owner || "—"}</b></span>
                  <span>Due: <b className="text-slate-800">{a.due_date || "—"}</b></span>
                </div>
                {linkedEv.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">{linkedEv.slice(0, 3).map((e) => <span key={e.id} className="text-[11px] rounded bg-emerald-50 border border-emerald-200 text-emerald-900 px-2 py-0.5">{e.title}</span>)}{linkedEv.length > 3 && <span className="text-[11px] text-slate-500">+{linkedEv.length - 3} more</span>}</div>
                )}
              </div>
              <div className="w-40">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Progress</div>
                <Progress value={a.progress || 0} className="h-2" />
                <div className="text-xs font-mono text-slate-700 mt-1">{a.progress || 0}%</div>
              </div>
              {canEdit(user?.role) && (
                <div className="flex flex-col gap-2 w-40">
                  <Select value={a.rag_status} onValueChange={(v) => updateAction(a.id, { rag_status: v })}>
                    <SelectTrigger data-testid={`row-rag-${a.id}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{RAG.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" min={0} max={100} value={a.progress || 0} onChange={(e) => updateAction(a.id, { progress: parseInt(e.target.value || 0) })} className="h-8 text-xs" />
                </div>
              )}
              {canManage(user?.role) && (
                <Button data-testid={`del-action-${a.id}`} variant="ghost" size="icon" onClick={() => del(a.id)}><Trash2 className="h-4 w-4 text-slate-500" /></Button>
              )}
            </div>
          </Card>
          );
        })}
      </div>

      <Dialog open={suggestOpen} onOpenChange={setSuggestOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Ofsted-aligned suggested actions</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Pick from the toolkit-derived improvement action library. Selected items become RAG-red actions in your QIP so you can assign owners and evidence.</p>
          </DialogHeader>
          <div className="flex items-center gap-2 flex-wrap py-2 border-b border-slate-100">
            <Select value={suggestTheme} onValueChange={openSuggestions}>
              <SelectTrigger data-testid="suggest-theme" className="w-60"><SelectValue /></SelectTrigger>
              <SelectContent>{themes.map((t) => <SelectItem key={t.key} value={t.key}>{t.title}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="suggest-owner" placeholder="Default owner (optional)" value={suggestOwner} onChange={(e) => setSuggestOwner(e.target.value)} className="flex-1 min-w-[160px]" />
            <Button variant="outline" size="sm" onClick={pickAll} data-testid="pick-all-btn"><CheckSquare className="h-3.5 w-3.5 mr-1" />Select all</Button>
            <span className="text-xs font-mono text-slate-500 ml-auto">{picked.size} / {suggestions.length}</span>
          </div>
          <div className="overflow-auto flex-1 -mx-6 px-6 py-2 space-y-2">
            {suggestions.map((s, i) => {
              const on = picked.has(i);
              return (
                <button data-testid={`suggest-${i}`} key={`${suggestTheme}-${i}-${s.title}`} type="button" onClick={() => togglePick(i)} className={`w-full text-left rounded-md border p-3 transition-colors ${on ? "border-indigo-400 bg-indigo-50/60" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
                  <div className="flex items-start gap-3">
                    {on ? <CheckSquare className="h-4 w-4 text-indigo-800 mt-0.5" /> : <Square className="h-4 w-4 text-slate-400 mt-0.5" />}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-slate-900">{s.title}</div>
                        {s.priority === "high" && <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px]">HIGH</Badge>}
                        {s.priority === "medium" && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">MED</Badge>}
                        {s.priority === "low" && <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">LOW</Badge>}
                      </div>
                      <div className="text-xs text-slate-600 mt-1 leading-relaxed">{s.description}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <DialogFooter className="border-t border-slate-100 pt-3">
            <Button variant="outline" onClick={() => setSuggestOpen(false)}>Cancel</Button>
            <Button data-testid="save-suggestions-btn" onClick={createFromSuggestions} disabled={suggestBusy || picked.size === 0} className="bg-indigo-900 hover:bg-indigo-950 text-white">{suggestBusy ? "Adding…" : `Add ${picked.size} to QIP`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
